import React, { type FunctionComponent } from 'react'
import { Alert, Button, NewSelect, PillSelector } from '@retailer-platform/shared-components'
import { AddIcon, CloseIcon } from '@instacart/ids-core'
import { Input } from '../../shared-styled-components/SharedStyledComponents.tsx'
import { Property, PropertyTitle, Spacer } from '../../../utils/common.styles.tsx'
import { useDomainMessages } from '../../../utils/domain/intl.ts'
import {
  ConfigPropertySection,
  ConfigSectionTitle,
} from './QualityAuditWorkflowConfigColumnModalFeatureInput.tsx'

interface Props {
  featureValue?: string
  onChange: (value: string) => void
}

interface FeatureConfigComponent {
  component_type: string
  component_name: string
}

interface FeatureConfigRecordAttributesToCatflow {
  batch_state?: string | null
  retailer_id_required?: string
  locale_id_required?: string
  product_id_component: FeatureConfigComponent
  retailer_id_component?: FeatureConfigComponent
  locale_id_component?: FeatureConfigComponent
  source_type?: string
  components: {
    component_type: string
    component_name: string
    catalog_attribute_name: string
  }[]
}

const SOURCE_TYPE_PRODUCT_EDIT_AUTOMATED_FIX = 'Product::Edit::AutomatedFix'

/* eslint-disable @typescript-eslint/naming-convention */
export const QualityAuditWorkflowConfigColumnModalFeatureInputRecordAttributeToCatflow: FunctionComponent<
  React.PropsWithChildren<Props>
> = ({ featureValue, onChange }) => {
  const i18n = useDomainMessages({
    retailerIdRequired:
      'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.recordCatflow.requireRetailerId',
    localeIdRequired:
      'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.recordCatflow.requireLocaleId',
    catalogDescription:
      'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.recordCatflow.attributeDescription',
    catalogAttributes:
      'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.recordCatflow.attributeTitle',
    entityTitle:
      'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.recordCatflow.entityTitle',
    entityDescription:
      'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.recordCatflow.entityDescription',
    attributeInfo:
      'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.recordCatflow.attributeInfo',
    entityNote:
      'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.recordCatflow.entityNote',
    entityRetailerId:
      'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.recordCatflow.entityRetailerId',
    entityBatchState:
      'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.recordCatflow.entityBatchState',
    sourceType:
      'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.recordCatflow.entitySourceType',
    localeId:
      'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.recordCatflow.entityLocaleId',
    productId:
      'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.recordCatflow.entityProductId',
    componentType:
      'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.recordCatflow.componentType',
    componentName:
      'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.recordCatflow.componentName',
    attributeName:
      'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.recordCatflow.attributeName',
  })

  let payload: FeatureConfigRecordAttributesToCatflow = {
    batch_state: null,
    product_id_component: {
      component_type: '',
      component_name: '',
    },
    components: [],
    source_type: SOURCE_TYPE_PRODUCT_EDIT_AUTOMATED_FIX,
  }
  try {
    payload = JSON.parse(featureValue ?? '')
  } catch (e) {
    // ignore, user will correct the input.
  }
  return (
    <>
      <ConfigPropertySection>
        <ConfigSectionTitle>{i18n.entityTitle}</ConfigSectionTitle>
        <ConfigPropertySection
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
          }}
        >
          <Property style={{ padding: 0 }}>
            <PropertyTitle>{i18n.retailerIdRequired}</PropertyTitle>
            <PillSelector
              items={[
                { label: 'True', value: 'true' },
                { label: 'False', value: 'false' },
              ]}
              value={`${payload?.retailer_id_required ?? false}`}
              onChange={e => {
                onChange(
                  JSON.stringify({
                    ...payload,
                    retailer_id_required: JSON.parse(e.target.value),
                    retailer_id_component: undefined,
                  })
                )
              }}
            />
          </Property>
          <Property style={{ padding: 0 }}>
            <PropertyTitle>{i18n.localeId}</PropertyTitle>
            <PillSelector
              items={[
                { label: 'True', value: 'true' },
                { label: 'False', value: 'false' },
              ]}
              value={`${payload?.locale_id_required ?? false}`}
              onChange={e => {
                onChange(
                  JSON.stringify({
                    ...payload,
                    locale_id_required: JSON.parse(e.target.value),
                    locale_id_component: undefined,
                  })
                )
              }}
            />
          </Property>
          <Property style={{ padding: 0 }}>
            <PropertyTitle>{i18n.entityBatchState}</PropertyTitle>
            <PillSelector
              items={[
                { label: 'Rejected', value: 'rejected' },
                { label: 'Approved', value: 'approved' },
                { label: 'Any', value: 'null' },
              ]}
              value={`${payload?.batch_state}`}
              onChange={e => {
                onChange(
                  JSON.stringify({
                    ...payload,
                    batch_state: e.target.value === 'null' ? null : e.target.value,
                  })
                )
              }}
            />
          </Property>
          <Property style={{ padding: 0 }}>
            <PropertyTitle>{i18n.sourceType}</PropertyTitle>
            <NewSelect
              options={[
                {
                  label: SOURCE_TYPE_PRODUCT_EDIT_AUTOMATED_FIX,
                  value: SOURCE_TYPE_PRODUCT_EDIT_AUTOMATED_FIX,
                },
              ]}
              value={payload?.source_type ?? SOURCE_TYPE_PRODUCT_EDIT_AUTOMATED_FIX}
              onChange={value => {
                onChange(
                  JSON.stringify({
                    ...payload,
                    source_type: value ? value : null,
                  } as FeatureConfigRecordAttributesToCatflow)
                )
              }}
            />
          </Property>
        </ConfigPropertySection>
        <Alert variant={'info'}>{i18n.entityDescription}</Alert>
        {((payload?.retailer_id_required ?? false) || (payload?.locale_id_required ?? false)) && (
          <Alert variant={'warning'}>{i18n.entityNote}</Alert>
        )}
        <ConfigPropertySection style={{ flexDirection: 'row', border: 0, padding: 0 }}>
          <Property style={{ flex: 1, padding: 0 }}>
            <PropertyTitle>
              {i18n.productId}- {i18n.componentType}
            </PropertyTitle>
            <Input
              onChange={e => {
                const value = e.target.value
                onChange(
                  JSON.stringify({
                    ...payload,
                    product_id_component: {
                      ...payload?.product_id_component,
                      component_type: value,
                    },
                  } as FeatureConfigRecordAttributesToCatflow)
                )
              }}
              value={payload?.product_id_component?.component_type ?? ''}
            />
          </Property>
          <Property style={{ flex: 1, padding: 0 }}>
            <PropertyTitle>
              {i18n.productId}- {i18n.componentName}
            </PropertyTitle>
            <Input
              onChange={e => {
                const value = e.target.value
                onChange(
                  JSON.stringify({
                    ...payload,
                    product_id_component: {
                      ...payload?.product_id_component,
                      component_name: value,
                    },
                  } as FeatureConfigRecordAttributesToCatflow)
                )
              }}
              value={payload?.product_id_component?.component_name ?? ''}
            />
          </Property>
        </ConfigPropertySection>

        {(payload?.retailer_id_required ?? false) && (
          <ConfigPropertySection style={{ flexDirection: 'row', border: 0, padding: 0 }}>
            <Property style={{ flex: 1, padding: 0 }}>
              <PropertyTitle>
                {i18n.entityRetailerId} - {i18n.componentType}
              </PropertyTitle>
              <Input
                onChange={e => {
                  const value = e.target.value
                  onChange(
                    JSON.stringify({
                      ...payload,
                      retailer_id_component: {
                        ...payload?.retailer_id_component,
                        component_type: value,
                      },
                    } as FeatureConfigRecordAttributesToCatflow)
                  )
                }}
                value={payload?.retailer_id_component?.component_type ?? ''}
              />
            </Property>
            <Property style={{ flex: 1, padding: 0 }}>
              <PropertyTitle>
                {i18n.entityRetailerId} - {i18n.componentName}
              </PropertyTitle>
              <Input
                onChange={e => {
                  const value = e.target.value
                  onChange(
                    JSON.stringify({
                      ...payload,
                      retailer_id_component: {
                        ...payload?.retailer_id_component,
                        component_name: value,
                      },
                    } as FeatureConfigRecordAttributesToCatflow)
                  )
                }}
                value={payload?.retailer_id_component?.component_name ?? ''}
              />
            </Property>
          </ConfigPropertySection>
        )}
        {(payload?.locale_id_required ?? false) && (
          <ConfigPropertySection style={{ flexDirection: 'row', border: 0, padding: 0 }}>
            <Property style={{ flex: 1, padding: 0 }}>
              <PropertyTitle>
                {i18n.localeId} - {i18n.componentType}
              </PropertyTitle>
              <Input
                onChange={e => {
                  const value = e.target.value
                  onChange(
                    JSON.stringify({
                      ...payload,
                      locale_id_component: {
                        ...payload?.locale_id_component,
                        component_type: value,
                      },
                    } as FeatureConfigRecordAttributesToCatflow)
                  )
                }}
                value={payload?.locale_id_component?.component_type ?? ''}
              />
            </Property>
            <Property style={{ flex: 1, padding: 0 }}>
              <PropertyTitle>
                {i18n.localeId} - {i18n.componentName}
              </PropertyTitle>
              <Input
                onChange={e => {
                  const value = e.target.value
                  onChange(
                    JSON.stringify({
                      ...payload,
                      locale_id_component: {
                        ...payload?.locale_id_component,
                        component_name: value,
                      },
                    } as FeatureConfigRecordAttributesToCatflow)
                  )
                }}
                value={payload?.locale_id_component?.component_name ?? ''}
              />
            </Property>
          </ConfigPropertySection>
        )}
      </ConfigPropertySection>
      <ConfigPropertySection>
        <ConfigSectionTitle>{i18n.catalogAttributes}</ConfigSectionTitle>
        <Alert variant={'info'}>{i18n.attributeInfo}</Alert>
        <div style={{ display: 'flex' }}>
          <Spacer />
          <Button
            variant={'tertiary'}
            style={{ margin: 0 }}
            compact
            onClick={() => {
              onChange(
                JSON.stringify({
                  ...payload,
                  components: [...(payload?.components ?? []), {}],
                } as FeatureConfigRecordAttributesToCatflow)
              )
            }}
          >
            <AddIcon size={18} color="systemSuccessRegular" />
          </Button>
        </div>
        {(payload?.components ?? []).map((value, index) => {
          const components = payload?.components ?? []
          const component = components?.[index]
          return (
            <ConfigPropertySection key={index} style={{ flexDirection: 'row' }}>
              <Property style={{ flex: 1, padding: 0 }}>
                <PropertyTitle>{i18n.attributeName}</PropertyTitle>
                <Input
                  onChange={e => {
                    const value = e.target.value
                    onChange(
                      JSON.stringify({
                        ...payload,
                        components: [
                          ...components.slice(0, index),
                          { ...component, catalog_attribute_name: value },
                          ...components.slice(index + 1),
                        ],
                      } as FeatureConfigRecordAttributesToCatflow)
                    )
                  }}
                  value={component?.catalog_attribute_name ?? ''}
                />
              </Property>
              <Property style={{ flex: 1, padding: 0 }}>
                <PropertyTitle>{i18n.componentType}</PropertyTitle>
                <Input
                  onChange={e => {
                    const value = e.target.value
                    onChange(
                      JSON.stringify({
                        ...payload,
                        components: [
                          ...components.slice(0, index),
                          { ...component, component_type: value },
                          ...components.slice(index + 1),
                        ],
                      } as FeatureConfigRecordAttributesToCatflow)
                    )
                  }}
                  value={component?.component_type ?? ''}
                />
              </Property>
              <Property style={{ flex: 1, padding: 0 }}>
                <PropertyTitle>{i18n.componentName}</PropertyTitle>
                <Input
                  onChange={e => {
                    const value = e.target.value
                    onChange(
                      JSON.stringify({
                        ...payload,
                        components: [
                          ...components.slice(0, index),
                          { ...component, component_name: value },
                          ...components.slice(index + 1),
                        ],
                      } as FeatureConfigRecordAttributesToCatflow)
                    )
                  }}
                  value={component?.component_name ?? ''}
                />
              </Property>
              <Button
                variant={'tertiary'}
                style={{ margin: 0 }}
                compact
                onClick={() => {
                  onChange(
                    JSON.stringify({
                      ...payload,
                      components: [...components.slice(0, index), ...components.slice(index + 1)],
                    } as FeatureConfigRecordAttributesToCatflow)
                  )
                }}
              >
                <CloseIcon size={18} color="systemSuccessRegular" />
              </Button>
            </ConfigPropertySection>
          )
        })}
      </ConfigPropertySection>
    </>
  )
}
/* eslint-enable @typescript-eslint/naming-convention */
